<script lang="ts">
import { computed, defineComponent, onMounted, ref } from "vue";

/** How long a held session is given to resume before the sign-in is drawn. */
const SETTLE_MS = 2500;
import { useSessionStore } from "@/stores/session";
import { OAUTH_CONSENT_ROUTE } from "@/nav/shellRoutes";
import Login from "./Login.vue";
import PreAuthPage from "../PreAuthPage.vue";
import { answerConsent, clearOAuthQuery, describeClient, oauthQuery, type OAuthClientView } from "../oauthFlow";

/**
 * 🔴 **ALLOW THIS CLIENT TO ACT AS YOU?** (D133). The one question the flow asks that the Desk's
 * sign-in does not: an MCP client — Claude Code, Grokbot — is about to hold a token that IS you on
 * the platform, with your authority. The client's name comes from the server's record of it, never
 * from the query; the answer goes to the server, which sends the browser back to the client.
 */
export default defineComponent({
  name: "OAuthConsent",
  components: { Login, PreAuthPage },
  setup() {
    const session = useSessionStore();
    const query = oauthQuery();
    const client = ref<OAuthClientView | null>(null);
    const busy = ref(false);
    const failed = ref(false);
    const ready = computed(() => session.status === "ready");
    const settled = ref(false);
    const settling = computed(() => !ready.value && (session.status === "resolving" || (session.status === "signed-out" && !settled.value)));

    onMounted(async () => {
      window.setTimeout(() => (settled.value = true), SETTLE_MS);
      if (query !== "") client.value = await describeClient(query);
    });

    async function answer(accept: boolean): Promise<void> {
      if (busy.value) return;
      busy.value = true;
      failed.value = false;
      const next = await answerConsent(query, accept);
      if (next === null) {
        failed.value = true;
        busy.value = false;
        return;
      }
      clearOAuthQuery();
      window.location.replace(next);
    }

    return { session, ready, settling, query, client, busy, failed, answer, callbackPath: OAUTH_CONSENT_ROUTE };
  },
});
</script>

<template>
  <div data-testid="oauth-consent">
    <p v-if="settling && query !== ''" class="consent__wait" data-testid="oauth-settling">One moment…</p>
    <Login v-else-if="!ready && query !== ''" :google-callback-path="callbackPath" />
    <PreAuthPage v-else>
      <div class="consent">
        <h1 class="consent__title">Allow this app to act as you on Pinksy?</h1>
        <p v-if="query === ''" data-testid="oauth-missing">Nothing to allow. Start again from the app that asked you to connect to Pinksy.</p>
        <template v-else>
          <p class="consent__client" data-testid="consent-client">
            <b>{{ client?.name ?? "An app" }}</b>
            <span v-if="client?.uri" class="consent__uri"> · {{ client.uri }}</span>
          </p>
          <p>
            It will hold a token that is <b>you</b>: what it can do on Pinksy is exactly what you can do, for as long as the token lasts, until you sign
            out everywhere or revoke it. It cannot see your password or your passkeys.
          </p>
          <p v-if="failed" class="consent__failed" data-testid="consent-failed">Pinksy did not accept the answer. Start again from the app.</p>
          <div class="consent__actions">
            <button type="button" class="consent__deny" :disabled="busy" data-testid="consent-deny" @click="answer(false)">Don't allow</button>
            <button type="button" class="consent__allow" :disabled="busy" data-testid="consent-allow" @click="answer(true)">{{ busy ? "…" : "Allow" }}</button>
          </div>
        </template>
      </div>
    </PreAuthPage>
  </div>
</template>

<style scoped>
.consent {
  max-width: 480px;
  margin: 0 auto;
  display: grid;
  gap: 14px;
}
.consent__title {
  font-size: 1.3rem;
  margin: 0;
}
.consent__client {
  font-size: 1.05rem;
  margin: 0;
}
.consent__uri {
  opacity: 0.7;
}
.consent__wait {
  margin: 20vh auto 0;
  text-align: center;
}
.consent__failed {
  color: var(--pinksy-danger, #c62828);
}
.consent__actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}
.consent__allow,
.consent__deny {
  padding: 10px 18px;
  border-radius: 8px;
  border: 1px solid currentColor;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
}
.consent__allow {
  background: var(--pinksy-primary, #e91e63);
  border-color: var(--pinksy-primary, #e91e63);
  color: #fff;
}
</style>
